import { useEffect, useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import useAuth from "./useAuth";
import { useSocket } from "../context/SocketContext";
import {
  fetchInbox,
  markAllInboxConversationsAsRead,
  markInboxConversationAsRead,
} from "../services/api/inbox";

/**
 * Conversation list for the top-bar inbox.
 *
 * Fetches on initial mount and when explicitly enabled (dropdown opens).
 * The mutation success handler updates the cache optimistically so the badge
 * count is immediate. No need to watch the notification API since the inbox
 * is now the source of truth for conversation unread counts.
 *
 * @param {boolean} enabled fetch when the dropdown opens
 */
export function useInbox({ enabled = true } = {}) {
  const { auth } = useAuth();
  const queryClient = useQueryClient();
  const socket = useSocket();

  const queryKey = useMemo(() => ["inbox", auth?.user?.id], [auth?.user?.id]);

  const {
    data,
    refetch,
    isPending: isInboxApiLoading,
    error: isInboxApiError,
  } = useQuery({
    queryKey,
    queryFn: () => fetchInbox({ limit: 20 }),
    enabled: !!auth?.user?.id && enabled,
  });

  const conversations = data?.data || [];
  const nextCursor = data?.nextCursor || null;
  const unreadConversationCount = data?.unreadConversationCount || 0;

  // Refetch when enabled (dropdown opens) to catch any changes that happened while closed.
  useEffect(() => {
    if (enabled) {
      refetch();
    }
  }, [enabled, refetch]);

  const loadMore = async () => {
    if (!nextCursor) return;
    const more = await fetchInbox({ cursor: nextCursor, limit: 20 });
    queryClient.setQueryData(queryKey, (old) =>
      old
        ? {
            ...old,
            data: [...(old.data || []), ...(more.data || [])],
            nextCursor: more.nextCursor,
          }
        : more,
    );
  };

  // Keep the badge honest between dropdown opens. The server pushes
  // `unreadCountUpdated` to the user whenever a conversation's count changes —
  // up on a new message, and down once they read or reply to it.
  //
  // The payload already carries the new count, so apply it straight to the
  // cache. Invalidating instead meant a full GET /inbox every time this fired,
  // and the server emits it to the *sender* too (with unreadCount: 0) — so
  // every message sent triggered a refetch from the always-mounted top bar.
  useEffect(() => {
    if (!socket || !auth?.user?.id) return;

    const applyUnreadCount = ({ conversationId, unreadCount }) => {
      if (conversationId == null) return;

      const nextUnread = unreadCount ?? 0;
      const cached = queryClient.getQueryData(queryKey);
      const conversations = cached?.data || [];
      const index = conversations.findIndex(
        (conversation) => conversation.conversationId === conversationId,
      );
      const previousUnread = index === -1 ? null : (conversations[index].unreadCount ?? 0);

      // Count went UP, or the conversation isn't on the cached page at all:
      // there's a message we don't have. Refetch so the preview, the ordering
      // and any conversation missing from the page all come with it.
      if (previousUnread === null || nextUnread > previousUnread) {
        queryClient.invalidateQueries({ queryKey });
        return;
      }

      // Unchanged — the common case when sending into a conversation that was
      // already read. Nothing to write, and crucially nothing to fetch.
      if (previousUnread === nextUnread) return;

      // Count went DOWN: the user read or replied. We already know the new
      // value, so apply it instead of asking the server for it again.
      queryClient.setQueryData(queryKey, (old) => {
        if (!old) return old;

        // `unreadConversationCount` spans every conversation, not just this
        // page, so it can't be recomputed from the cache — step it down by one.
        const unreadConversations =
          nextUnread === 0
            ? Math.max(0, (old.unreadConversationCount ?? 0) - 1)
            : (old.unreadConversationCount ?? 0);

        const updated = [...(old.data || [])];
        updated[index] = { ...updated[index], unreadCount: nextUnread };

        return { ...old, data: updated, unreadConversationCount: unreadConversations };
      });
    };

    socket.on("unreadCountUpdated", applyUnreadCount);
    return () => socket.off("unreadCountUpdated", applyUnreadCount);
  }, [socket, auth?.user?.id, queryClient, queryKey]);

  const { mutateAsync: markConversationAsReadMutation } = useMutation({
    mutationFn: markInboxConversationAsRead,
    onSuccess: (response, conversationId) => {
      queryClient.setQueryData(queryKey, (old) => {
        if (!old) return old;
        const updated = (old.data || []).map((conversation) =>
          conversation.conversationId === conversationId
            ? { ...conversation, unreadCount: 0 }
            : conversation,
        );
        const newUnreadCount =
          response?.data?.unreadConversationCount ?? old.unreadConversationCount;
        return {
          ...old,
          data: updated,
          // Use the actual count from the backend, not the local cache page count
          unreadConversationCount: newUnreadCount,
        };
      });
    },
  });

  const { mutateAsync: markAllAsRead, isPending: isMarkingAllAsRead } = useMutation({
    mutationFn: markAllInboxConversationsAsRead,
    onSuccess: (response) => {
      queryClient.setQueryData(queryKey, (old) => {
        if (!old) return old;

        return {
          ...old,
          data: (old.data || []).map((conversation) => ({ ...conversation, unreadCount: 0 })),
          unreadConversationCount: response?.data?.unreadConversationCount ?? 0,
        };
      });
    },
  });

  return {
    conversations,
    nextCursor,
    unreadConversationCount,
    isInboxApiLoading,
    isInboxApiError,
    isMarkingAllAsRead,
    loadMore,
    refetchInbox: refetch,
    markConversationAsReadMutation,
    markAllAsRead,
  };
}
